import { Icon } from '@amzn/awsui-components-core';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';
import { useEffect, useState } from 'react';
import '../../resources/styles/scss/components/ContentPageTree.css';
import { Page } from '../../model/Page';
import { useEntityConverter } from '../v2-hooks/useEntityConverter';
import { navigationLabelForNode } from '../v2-common/ContentNodeExtensions';

type InitialTreeState = 'default' | 'expand-all' | 'collapse-all';

interface ContentPageTreeProps {
  readonly pages: readonly Page[];
  readonly selectedNode?: ContentNode;
  readonly onSelectedNodeChainChanged?: (selectedNode: ContentNode, depth: number) => void;
  readonly initialTreeState?: InitialTreeState;
}

/** Returns true if the given page (or any descendant) matches the selected node id. */
function containsNode(page: Page, nodeId: string | undefined): boolean {
  if (!nodeId) return false;
  if (page.node.id === nodeId) return true;
  return page.children.some((child) => containsNode(child, nodeId));
}

interface ContentPageTreeNodeProps {
  readonly page: Page;
  readonly selectedNode?: ContentNode;
  readonly onNodeSelected?: (node: ContentNode, depth: number) => void;
  readonly initialTreeState?: InitialTreeState;
}

/** Renders a single node in the content page tree with expand/collapse and selection support. */
function ContentPageTreeNode({
  page,
  selectedNode,
  onNodeSelected,
  initialTreeState,
}: ContentPageTreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const entityConverter = useEntityConverter();

  useEffect(() => {
    if (initialTreeState === 'collapse-all') {
      setIsExpanded(false);
    } else if (initialTreeState === 'expand-all') {
      setIsExpanded(true);
    }
  }, [initialTreeState]);

  const node = page.node;
  const depth = page.depth;
  const isNodeSelected = node.id === selectedNode?.id;
  const hasChildren = page.children.length > 0;
  const isChildNodeSelected = page.children.some((child) => containsNode(child, selectedNode?.id));

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const selectNode = () => {
    onNodeSelected?.(node, depth);
  };

  const shouldShowChildren = isExpanded || isChildNodeSelected;
  const nodeClassName = isNodeSelected ? 'parent-page-selected' : 'parent-page-item';
  const caretIcon = isExpanded ? 'caret-down-filled' : 'caret-right-filled';

  const titleText = navigationLabelForNode(node, entityConverter);
  const displayTitle = titleText.trim() === '' ? '<empty>' : titleText;

  return (
    <div className="tree-section">
      <div className={`${nodeClassName} display-flex-row`} lang="en">
        {hasChildren && (
          <span className="expand-icon" onClick={toggleExpanded}>
            <Icon name={caretIcon} />
          </span>
        )}
        <span className="page-title" onClick={selectNode}>
          {displayTitle}
        </span>
      </div>
      {shouldShowChildren && (
        <div className="child-nodes">
          {page.children.map((child) => (
            <ContentPageTreeNode
              key={child.node.href || child.node.id}
              page={child}
              selectedNode={selectedNode}
              onNodeSelected={onNodeSelected}
              initialTreeState={initialTreeState}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Renders a hierarchical tree of content pages with selection and expand/collapse controls. */
export function ContentPageTree({
  pages,
  selectedNode,
  onSelectedNodeChainChanged,
  initialTreeState,
}: ContentPageTreeProps) {
  return (
    <div className="content-page-tree">
      {pages.map((page) => (
        <ContentPageTreeNode
          key={page.node.href || page.node.id}
          page={page}
          selectedNode={selectedNode}
          onNodeSelected={onSelectedNodeChainChanged}
          initialTreeState={initialTreeState}
        />
      ))}
    </div>
  );
}
